export type LegalDocumentId = 'privacy' | 'terms'

export type LegalSection = {
  heading: string
  body: string
}

export type LegalDocument = {
  title: string
  updated: string
  intro: string
  sections: LegalSection[]
}

export const LEGAL_DOCUMENTS: Record<LegalDocumentId, LegalDocument> = {
  privacy: {
    title: 'Privacy Policy',
    updated: 'June 2025',
    intro:
      'TalentCo ("we", "us") operates the TalentCo recruiting platform. This policy describes how we collect, use, and protect personal information when you use our services, including sign-in with LinkedIn and optional job sharing features.',
    sections: [
      {
        heading: 'Information we collect',
        body:
          'Account information (name, email, role), profile and organisation data you provide, job and application data, usage logs, and cookies/session data required to operate the platform. When you use LinkedIn sign-in, we receive identity information permitted by LinkedIn (such as name and email) via OpenID Connect. When you connect LinkedIn for job sharing, we store OAuth tokens needed to post on your behalf until you disconnect.',
      },
      {
        heading: 'How we use information',
        body:
          'To provide authentication, job publishing, applicant tracking, and related recruiting workflows; to improve the product; to comply with law; and, with your action, to share public job links to your LinkedIn profile when you request it.',
      },
      {
        heading: 'Third-party services',
        body:
          'We use identity providers (Google, LinkedIn), email delivery, hosting, and analytics providers. Their use of data is governed by their policies. LinkedIn data is used only for sign-in and sharing features you initiate.',
      },
      {
        heading: 'Retention and security',
        body:
          'We retain data while your account is active and as required for legal or operational purposes. We apply reasonable technical and organisational measures to protect data. No method of transmission over the Internet is fully secure.',
      },
      {
        heading: 'Your rights',
        body:
          'Depending on your location, you may have rights to access, correct, delete, or export personal data, or to object to certain processing. Contact us using the details below to make a request.',
      },
      {
        heading: 'Contact',
        body:
          'For privacy questions, contact TalentCo at privacy@talentco.io. This policy may be updated; material changes will be reflected on this page.',
      },
    ],
  },
  terms: {
    title: 'Terms of Service',
    updated: 'June 2025',
    intro:
      'These Terms govern access to and use of the TalentCo platform. By creating an account or using the service, you agree to these Terms.',
    sections: [
      {
        heading: 'Use of the platform',
        body:
          'You must provide accurate information, keep credentials secure, and use TalentCo only for lawful recruiting and employment-related purposes. You are responsible for content you publish, including job listings and materials shared to third-party networks.',
      },
      {
        heading: 'Employer responsibilities',
        body:
          'Employers must have authority to post roles on behalf of their organisation, comply with applicable employment and anti-discrimination laws, and ensure job postings are accurate and not misleading.',
      },
      {
        heading: 'Third-party sign-in and sharing',
        body:
          'Optional LinkedIn and Google sign-in are subject to those providers\' terms as well. When you authorise sharing to LinkedIn, you grant TalentCo permission to post the content you approve to your LinkedIn profile using LinkedIn\'s APIs.',
      },
      {
        heading: 'Intellectual property',
        body:
          'TalentCo retains rights in the platform. You retain rights in your content; you grant us a licence to host, display, and syndicate job content as needed to operate the service.',
      },
      {
        heading: 'Limitation of liability',
        body:
          'The platform is provided "as is" to the extent permitted by law. TalentCo is not liable for indirect or consequential damages arising from use of the service or third-party integrations.',
      },
      {
        heading: 'Contact',
        body:
          'Questions about these Terms: legal@talentco.io. We may update these Terms; continued use after changes constitutes acceptance of the revised Terms.',
      },
    ],
  },
}
